import React, { useEffect, useState, forwardRef } from "react";
import { Chart, ChartCanvas } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { discontinuousTimeScaleProviderBuilder } from "@react-financial-charts/scales";
import { MACDSeries } from "@react-financial-charts/series";
import { lastVisibleItemBasedZoomAnchor } from "react-financial-charts";
import { format } from "d3-format";
import { calculatePVO } from "../../../utils/indicatorCalculations";

const PVOIndicator = forwardRef(({
  height,
  ratio,
  width,
  initialData,
  xAccessor,
  displayXAccessor,
  xScale,
  xExtents,
  showXAxis = true,
  fast = 12,
  slow = 26,
  signal = 9,
  margin,
  padding,
}, ref) => {
  const [calculatedData, setCalculatedData] = useState([]);
  const xScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date);

  const macdAppearance = {
    fillStyle: {
      divergence: "#4d98ba",
    },
    strokeStyle: {
      macd: "#f95e6b",
      signal: "#201f20",
      zero: "rgba(0, 0, 0, 0.3)",
    },
  };

  useEffect(() => {
    if (!initialData || initialData.length === 0) {
      setCalculatedData([]);
      return;
    }
    const formattedData = initialData.map((item) => ({
      ...item,
      date: new Date(item.timestamp || item.date),
    }));
    const result = calculatePVO(formattedData, fast, slow, signal);
    // Map to MACD-compatible structure for MACDSeries rendering
    const mapped = result.map(d => ({
      ...d,
      macd: {
        macd: d.PVO,
        signal: d.PVOs,
        divergence: d.PVOh,
      },
    }));
    setCalculatedData(mapped);
  }, [initialData, fast, slow, signal]);

  const { data } = xScaleProvider(calculatedData);
  const yAccessor = (d) => d?.macd;

  const calculateOptimalTicks = (chartHeight) => {
    const baseTickNumber = Math.floor(chartHeight / 50);
    return Math.max(baseTickNumber, 3);
  };
  const optimalTicks = calculateOptimalTicks(height);

  if (!data || data.length === 0) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#666' }}>Loading PVO...</span>
      </div>
    );
  }

  return (
    <ChartCanvas
      ref={ref}
      height={height}
      ratio={ratio}
      width={width}
      disableZoom={false}
      disablePan={false}
      disableInteraction={false}
      clamp={false}
      zoomAnchor={lastVisibleItemBasedZoomAnchor}
      data={data}
      displayXAccessor={displayXAccessor}
      seriesName="Data"
      xScale={xScale}
      xAccessor={xAccessor}
      xExtents={xExtents}
      margin={margin}
      padding={padding}
    >
      {/* Client: "When you have two indicators, the top of the top indicator and
          the bottom of the bottom indicator are cut off. You have to scroll out
          on the chart to see the RSI under 30 and the ADL at its current level.
          Can we add some padding without messing up these indicators staying
          connected to price when a user scrolls in or out?"

          `padding` on <Chart> is exactly that guarantee. It shrinks the y RANGE
          by 8px at each end — the plot is drawn 16px shorter inside the same
          panel — and leaves the y DOMAIN, the x scale and xExtents untouched.
          Horizontal sync with the price chart is driven entirely by the shared
          xScale/xExtents, so panning and zooming stay locked to price exactly as
          before; only the vertical breathing room changes. */}
      <Chart id={1} yExtents={yAccessor} padding={{ top: 8, bottom: 8 }}>
        {showXAxis && <XAxis strokeStyle={"#e5e7eb"} tickStrokeStyle={"#e5e7eb"} />}
        {!showXAxis && (
          <XAxis
            strokeStyle={"#e5e7eb"}
            tickStrokeStyle={"#e5e7eb"}
            showTicks={false}
            showTickLabel={false}
            xZoomHeight={1}
            tickPadding={0}
          />
        )}
        <YAxis
          strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"}
          tickFormat={format(".2f")}
          ticks={optimalTicks}
        />
        <XAxis
          strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"}
          showTicks={false}
          xZoomHeight={1}
          tickPadding={0}
          axisAt={"top"}
          orient={"top"}
        />
        <YAxis
          strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"}
          showTicks={false}
          yZoomWidth={1}
          tickPadding={0}
          axisAt={"left"}
          orient={"left"}
        />

        <MACDSeries yAccessor={yAccessor} {...macdAppearance} />
      </Chart>
    </ChartCanvas>
  );
});

export default PVOIndicator;
